/**
 * App Navigation Hook
 * 
 * Maps legacy DashboardView names to URL paths and provides navigation helpers.
 * Used by components that still reference the old view-based navigation.
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';

/** Legacy view type — kept for backward compatibility with existing components */
export type ViewType =
  | 'dashboard'
  | 'form'
  | 'list'
  | 'results'
  | 'discover'
  | 'library'
  | 'settings'
  | 'admin'
  | 'team'
  | 'onboarding'
  | 'tickets'
  | 'deploy'
  | 'exchanges'
  | 'billing';

/** Map from legacy view name → URL path */
const VIEW_TO_PATH: Record<ViewType, string> = {
  dashboard:  '/dashboard',
  form:       '/dashboard/library',
  list:       '/dashboard/workflows',
  results:    '/dashboard/workflows',  // needs /:jobId appended via navigateToResults
  discover:   '/dashboard/library',
  library:    '/dashboard/library',
  settings:   '/dashboard/settings',
  admin:      '/dashboard/admin',
  team:       '/dashboard/team',
  onboarding: '/dashboard/onboarding',
  tickets:    '/dashboard/support',
  deploy:     '/dashboard/deploy',
  exchanges:  '/dashboard/exchanges',
  billing:    '/dashboard/billing',
};

/** Map from URL path segment → breadcrumb label */
export const PATH_LABELS: Record<string, string> = {
  dashboard:  'Dashboard',
  backtest:   'Backtest',
  new:        'New',
  research:   'Research',
  discover:   'Discover',
  library:    'Library',
  lab:        'Research',
  jobs:       'Research',
  data:       'Data',
  settings:   'Settings',
  admin:      'Admin',
  team:       'Team',
  onboarding: 'Onboarding',
  support:    'Support',
  deploy:     'Live Trading',
  exchanges:  'Exchanges',
  billing:    'Billing',
};

export interface AppNavigate {
  /** Navigate to a legacy view by name */
  navigateToView: (view: ViewType) => void;
  /** Navigate to job results */
  navigateToResults: (jobId: string) => void;
  /** Navigate back to jobs list */
  navigateToJobs: () => void;
  /** Navigate to dashboard */
  navigateToDashboard: () => void;
  /** Get the path for a view */
  getViewPath: (view: ViewType) => string;
  /** Get breadcrumb segments from current path */
  breadcrumbs: { label: string; path: string }[];
}

export function useAppNavigate(): AppNavigate {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToView = useCallback(
    (view: ViewType) => {
      navigate(VIEW_TO_PATH[view]);
    },
    [navigate],
  );

  const navigateToResults = useCallback(
    (jobId: string) => {
      // Individual backtest results live under /dashboard/workflows/:id
      // (kept as the results URL after the agentic workflow backend's
      // retirement -- see WorkflowsPage's own doc comment). WorkflowsPage
      // hands :id straight to ResultsVisualization as a backtest job id.
      navigate(`/dashboard/workflows/${jobId}`);
    },
    [navigate],
  );

  const navigateToJobs = useCallback(() => {
    navigate('/dashboard/workflows');
  }, [navigate]);

  const navigateToDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const getViewPath = useCallback((view: ViewType) => VIEW_TO_PATH[view], []);

  // Build breadcrumbs from current pathname
  const breadcrumbs = buildBreadcrumbs(location.pathname);

  return {
    navigateToView,
    navigateToResults,
    navigateToJobs,
    navigateToDashboard,
    getViewPath,
    breadcrumbs,
  };
}

function buildBreadcrumbs(pathname: string): { label: string; path: string }[] {
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);
  const crumbs: { label: string; path: string }[] = [];

  let currentPath = '';
  for (const seg of segments) {
    currentPath += `/${seg}`;
    const label = PATH_LABELS[seg] || seg;
    crumbs.push({ label, path: currentPath });
  }

  return crumbs;
}
